import posthog, { DisplaySurveyType, type PostHogConfig } from 'posthog-js';
import { FEEDBACK_SURVEY_ID, hasFeedbackSurvey, isFeedbackCompletion } from '../../core/feedback';
import { createFeedbackCooldown } from './feedback-cooldown';
import { LABS_EXCEPTION_CAPTURE } from '../../../../shared/exception-scrubber';
import { prepareBrowserEvent } from './privacy';
import { createSubscriptionEnricher, type GetToken } from '../../../../shared/sgapp-subscription';
import { posthogEventName, type AnalyticsEvent } from '../../core/events';
export interface Analytics {
  track(event: AnalyticsEvent): void;
  watchFeedback?(onChange: (available: boolean) => void): () => void;
  openFeedback?(): void;
  dispose(): void;
}
export const nullAnalytics = (): Analytics => ({ track() {}, dispose() {} });
const quietly = (fn: () => void) => { try { fn(); } catch { /* Measurement must never interrupt teaching. */ } };
const interactions: Partial<PostHogConfig> = {
  capture_pageview: 'history_change', capture_pageleave: true, capture_heatmaps: true,
  autocapture: { dom_event_allowlist: ['click'], capture_copied_text: false },
};
export function createAnalytics(userId: string, email: string | null, getToken: GetToken): Analytics {
  if (!import.meta.env.VITE_POSTHOG_KEY) return nullAnalytics();
  const subscription = createSubscriptionEnricher({ hostname: window.location.hostname, enrichAll: true });
  subscription.setIdentity(userId, getToken);
  let active = true;
  let stopWatching = () => {};
  const feedbackCooldown = createFeedbackCooldown(userId);
  let updateFeedback = () => {};
  let stopCapturing = () => {};
  quietly(() => {
    posthog.init(import.meta.env.VITE_POSTHOG_KEY!, {
      api_host: import.meta.env.VITE_POSTHOG_HOST ?? 'https://s.240.org',
      ui_host: 'https://us.posthog.com',
      defaults: '2026-05-30',
      ...interactions, person_profiles: 'identified_only',
      mask_all_text: true, mask_all_element_attributes: true,
      disable_surveys: false, disable_session_recording: false,
      enable_recording_console_log: false, capture_dead_clicks: false,
      // Errors are on, scrubbed first. See shared/exception-scrubber.ts.
      capture_exceptions: LABS_EXCEPTION_CAPTURE, before_send: prepareBrowserEvent,
      capture_performance: false,
      session_recording: {
        maskAllInputs: true, maskTextSelector: '.ph-mask', blockClass: 'ph-no-capture',
        blockSelector: '.cl-rootBox, .cl-userButtonPopoverCard', recordHeaders: false,
        recordBody: false, recordCrossOriginIframes: false, captureCanvas: { recordCanvas: false },
        maskCapturedNetworkRequestFn: request => ({ ...request, name: request.name.split(/[?#]/)[0]! }),
      },
    });
    posthog.identify(userId, email ? { email } : {});
    // init is a no-op on account changes; restore capture stopped by dispose.
    posthog.set_config(interactions);
    // This shared project's URL trigger targets another product. Start OSA locally,
    // retaining the project's sampling and all configured masking/blocking rules.
    posthog.startSessionRecording({ url_trigger: true });
  });
  quietly(() => {
    // A subscription per adapter also survives account changes: repeated SDK init is a no-op.
    stopCapturing = posthog.on('eventCaptured', event => {
      if (active && isFeedbackCompletion(event) && event?.properties.distinct_id === userId) {
        feedbackCooldown.complete();
        quietly(updateFeedback);
      }
    });
  });
  return {
    watchFeedback(onChange) {
      stopWatching();
      onChange(false);
      let watching = true;
      let unsubscribe = () => {};
      let matching = false;
      let timer: ReturnType<typeof setTimeout> | undefined;
      const publish = () => {
        if (!active || !watching) return;
        clearTimeout(timer);
        const remaining = feedbackCooldown.remaining();
        onChange(matching && remaining === 0);
        if (remaining > 0) timer = setTimeout(publish, remaining);
      };
      const storageChanged = (event: StorageEvent) => {
        if (event.key === feedbackCooldown.key || event.key === null) publish();
      };
      updateFeedback = publish;
      window.addEventListener('storage', storageChanged);
      window.addEventListener('focus', publish);
      document.addEventListener('visibilitychange', publish);
      quietly(() => {
        unsubscribe = posthog.onSurveysLoaded(() => {
          if (!active || !watching) return;
          quietly(() => posthog.getActiveMatchingSurveys(surveys => {
            if (active && watching) { matching = hasFeedbackSurvey(surveys); publish(); }
          }));
        });
      });
      const stop = () => {
        watching = false; clearTimeout(timer); quietly(unsubscribe);
        window.removeEventListener('storage', storageChanged);
        window.removeEventListener('focus', publish);
        document.removeEventListener('visibilitychange', publish);
        if (updateFeedback === publish) updateFeedback = () => {};
      };
      stopWatching = stop;
      return stop;
    },
    openFeedback() {
      if (!active || feedbackCooldown.remaining() > 0) return;
      quietly(() => posthog.getActiveMatchingSurveys(surveys => {
        if (!active || feedbackCooldown.remaining() > 0 || !hasFeedbackSurvey(surveys)) return;
        quietly(() => posthog.displaySurvey(FEEDBACK_SURVEY_ID, {
          displayType: DisplaySurveyType.Popover, ignoreConditions: false, ignoreDelay: false,
          properties: { product: 'preview_osa', event_source: 'browser' },
        }));
      }));
    },
    track(event) {
      if (!active) return;
      quietly(() => subscription.track(event, (enriched, timestamp) => quietly(() => {
        if (active) posthog.capture(posthogEventName(event.name), { ...enriched.properties, product: 'preview_osa', event_source: 'browser', ...(email ? { $set: { email } } : {}) }, timestamp ? { timestamp } : undefined);
      })));
    },
    dispose() { active = false; stopWatching(); quietly(stopCapturing); subscription.dispose(); quietly(() => {
      posthog.cancelPendingSurvey(FEEDBACK_SURVEY_ID);
      // The SDK reset clears persistence, but does not dismiss a rendered popover.
      document.querySelector(`.PostHogSurvey-${FEEDBACK_SURVEY_ID}`)?.shadowRoot?.querySelector<HTMLButtonElement>('button[aria-label="Close survey"]')?.click();
    }); quietly(() => { posthog.set_config({ autocapture: false, capture_heatmaps: false, capture_pageview: false, capture_pageleave: false }); });
    quietly(() => { posthog.stopSessionRecording(); posthog.reset(); }); },
  };
}
export function posthogHeaders(): Record<string, string> {
  if (!import.meta.env.VITE_POSTHOG_KEY) return {};
  try {
    const id = posthog.get_distinct_id(); const session = posthog.get_session_id();
    return id && session ? { 'X-POSTHOG-DISTINCT-ID': id, 'X-POSTHOG-SESSION-ID': session } : {};
  } catch { return {}; }
}
